import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {TableButtonConfig} from "../../model/api/table-button-config";
import {ExportAsService} from "ngx-export-as";
import {Menu} from "primeng/menu";
import {ScrollerScrollEvent} from "primeng/scroller/scroller.interface";
import {TableScroll} from "../../model/api/table-scroll";
import {auditTime, Subject} from "rxjs";
import {CcVscrollTableBaseComponent} from "./cc-vscroll-table-base.component";

export type SelectionType = 'CUSTOM' | 'CTRLA';

@Component({
  selector: 'app-cc-vscroll-table',
  templateUrl: './cc-vscroll-table.component.html',
  styleUrls: ['./cc-vscroll-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CcVscrollTableComponent extends CcVscrollTableBaseComponent {

  Math = Math;

  tableValueMap = {};

  @ViewChildren('tableMenu')
  tableMenus: QueryList<Menu>;

  @Input()
  headerButtonConfig: TableButtonConfig[] = [];

  @Input()
  footerButtonConfig: TableButtonConfig[] = [];

  @Input()
  contextMenuConfig: MenuItem[];
  @Output()
  onContextMenuShow = new EventEmitter<any>();

  @Input()
  filterValue;

  @Input()
  recordIds: string[] = [];

  @Input()
  headerTitle: String;

  selectedRecord: any;

  @Output()
  onColOrderChange = new EventEmitter<any>();

  @Input()
  selectedIds: string[] = [];
  @Output()
  selectedIdsChange = new EventEmitter<string[]>();

  @Input()
  selectionType: SelectionType = 'CUSTOM';
  @Output()
  selectionTypeChange = new EventEmitter<SelectionType>();

  @Input()
  tableScroll: TableScroll = {left: 0, top: 0};
  @Output()
  tableScrollChange = new EventEmitter<TableScroll>();

  lastRecordsIndexClicked: number = -1;
  lastShiftRecordsIndexClicked: number = -1;

  public mouseDown: boolean = false;
  lastMouseSelect: boolean = false;

  complexFilterMode = false;

  @Input()
  scrollHeightNumber: number = 250;
  @Input()
  scrollHeightString: string = undefined;

  @Input()
  lazyLoadOnInit: boolean = false;

  private ctrlADone: boolean = false;

  private selectedIdsDebounceSubject = new Subject<string[]>();

  lazyLoadSubject = new Subject<any>();
  private lazyLoadcount = 0;

  constructor(el: ElementRef,
              cdref: ChangeDetectorRef,
              messageService: MessageService,
              translateService: TranslateService,
              exportAsService: ExportAsService) {
    super(el, cdref, messageService, translateService, exportAsService);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    const viewportScrollable = document.getElementsByTagName('cdk-virtual-scroll-viewport');
    if (viewportScrollable && viewportScrollable[0])
      viewportScrollable[0].classList.add('p-datatable-scrollable-body');

  }

  ngOnInit() {
    super.ngOnInit();
    this.lazyLoadSubject
      .pipe(auditTime(0),
      )  // You can tweak this delay
      .subscribe(event => {
        if (!event || !this.shouldLoad(event)) return;

        this.lastLazyLoadEvent = JSON.parse(JSON.stringify(event));
        this.loadRecords(event);
        this.lazyLoadcount++;
      });
    this.selectedIdsDebounceSubject
      .subscribe((selectedIds) => {
        this.selectionTypeChange.emit(this.selectionType);
        this.selectedIdsChange.emit(selectedIds);
      });
  }

  private lastLazyLoadEvent: any;

  private shouldLoad(event: any): boolean {
    if (!this.lastLazyLoadEvent) {
      return true;
    }

    const isDifferent = (
      this.lastLazyLoadEvent.first !== event.first ||
      this.lastLazyLoadEvent.rows !== event.rows ||
      this.lastLazyLoadEvent.sortField !== event.sortField ||
      this.lastLazyLoadEvent.sortOrder !== event.sortOrder ||
      this.lastLazyLoadEvent.globalFilter !== event.globalFilter ||
      this.lastLazyLoadEvent.stateFilter !== event.stateFilter ||
      (JSON.stringify(this.lastLazyLoadEvent.filters) !== JSON.stringify(event.filters))
    );

    return isDifferent;
  }

  onLazyLoadDebounced(event: any): void {
    this.lazyLoadSubject.next(event);
  }

  protected initData() {
    super.initData();
    if (this.ccTable) {
      this.ccTable.scroller.onScroll.subscribe((event: ScrollerScrollEvent) => {
        this.tableScroll.left = event.originalEvent.currentTarget['scrollLeft'];
        this.tableScroll.top = event.originalEvent.currentTarget['scrollTop'];
        this.tableScrollChange.emit(this.tableScroll);

      })

    }
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (changes.contextMenuConfig || changes.headerButtonConfig
      || changes.tableConfig || changes.currentRequest || changes.recordIds) {
      this.reloadTableValueMap();
    }
  }

  onRowDblClick_(record: any) {
    this.onRowDblClick.emit(record);
  }

  ngOnDestroy() {
    this.recordIds.splice(0);
    this.records.splice(0);
  }

  onRowRightClick_(record: any) {
    this.onRowRightClick.emit(record);
  }

  contextMenuShow($event: any) {
    if (this.disableContextMenu || this.disableSelection) return;

    this.onContextMenuShow.emit($event);
  }

  selectKeyUp($event: KeyboardEvent) {
    if (this.lastRecordsIndexClicked == -1) {
      this.lastRecordsIndexClicked = this.focusedRowIndex == 0 ? 1 : this.focusedRowIndex;
    }
    if (this.lastRecordsIndexClicked > 0) {
      this.lastRecordsIndexClicked--;
      const recordId = this.recordIds[this.lastRecordsIndexClicked];
      this.doSelect(recordId, this.lastRecordsIndexClicked, $event.shiftKey, $event.ctrlKey);
      this.lastMouseSelect = this.selectedIds.includes(recordId);
      $event.preventDefault();
      const tableId = this.el.nativeElement.querySelectorAll("table").item(0).id;
      const elm = document.querySelectorAll('#' + tableId + ' #' + recordId);
      if (elm && elm.length > 0)
        elm[0].scrollIntoView({behavior: 'smooth', block: 'center', inline: "nearest"});
      else {
        queueMicrotask(() => this.ccTable.scrollToVirtualIndex(this.lastRecordsIndexClicked));
      }
    }
  }

  selectKeyDown($event: KeyboardEvent) {
    if (this.lastRecordsIndexClicked == -1) {
      this.lastRecordsIndexClicked = this.focusedRowIndex == 0 ? -1 : this.focusedRowIndex;
    }
    if (this.lastRecordsIndexClicked < this.recordIds.length - 1) {
      this.lastRecordsIndexClicked++;
      const recordId = this.recordIds[this.lastRecordsIndexClicked];
      this.doSelect(recordId, this.lastRecordsIndexClicked, $event.shiftKey, $event.ctrlKey);
      this.lastMouseSelect = this.selectedIds.includes(recordId);
      $event.preventDefault();
      $event.stopPropagation();
      const tableId = this.el.nativeElement.querySelectorAll("table").item(0).id;
      const elm = document.querySelectorAll('#' + tableId + ' #' + recordId);
      if (elm && elm.length > 0)
        elm[0].scrollIntoView({behavior: 'smooth', block: 'center', inline: "nearest"});
      else {
        queueMicrotask(() => this.ccTable.scrollToVirtualIndex(this.lastRecordsIndexClicked));
      }
    }
  }

  onRowMouseUp(clickedRecordId: string, clickedRecordsIndex: number, $event: MouseEvent) {
    if (this.disableSelection) return;
    if (this.mouseDown && $event.button == 0 && this.lastMouseSelect
      && clickedRecordsIndex != this.lastRecordsIndexClicked) {
      this.lastShiftRecordsIndexClicked = this.lastRecordsIndexClicked;
      this.doSelect(clickedRecordId, clickedRecordsIndex, true, false);
    }
  }

  onRowMouseDown(recordId: string, clickedRecordsIndex: number, $event: MouseEvent) {
    if (this.disableSelection) return;
    if (this.tableContextMn != null)
      this.tableContextMn.hide();
    if (!this.mouseDown && ($event.button == 0
      || ($event.button == 2 && !this.selectedIds.includes(recordId)))) {
      this.doSelect(recordId, clickedRecordsIndex, $event.shiftKey, $event.ctrlKey);
      this.lastMouseSelect = this.selectedIds.includes(recordId);
    }
  }

  doSelect(clickedRecordId: string, clickedRecordsIndex: number
    , shiftPressed: boolean, ctrlPressed: boolean) {
    if (this.disableSelection) return;
    if (!ctrlPressed && !shiftPressed && this.selectedIds.length > 0) {
      this.clearSelection(false);
    }
    const clickedSelectedIndex = this.selectedIds.indexOf(clickedRecordId);
    if (clickedSelectedIndex != -1 && !shiftPressed) {
      const record = this.records.find(value => value && value.id === this.selectedIds[clickedSelectedIndex]);
      if (record) record.selected = false;
      this.selectedIds.splice(clickedSelectedIndex, 1);
    } else {
      if (clickedSelectedIndex == -1) {
        const record = this.records[clickedRecordsIndex];
        if (record) record.selected = true;
        this.selectedIds.push(clickedRecordId);
      }
      if (shiftPressed && this.lastRecordsIndexClicked !== -1) {
        if (this.lastShiftRecordsIndexClicked !== -1 &&
          ((clickedRecordsIndex < this.lastRecordsIndexClicked && this.lastShiftRecordsIndexClicked > this.lastRecordsIndexClicked)
            || (clickedRecordsIndex > this.lastRecordsIndexClicked && this.lastShiftRecordsIndexClicked < this.lastRecordsIndexClicked))) {
          const firstIndex = this.lastShiftRecordsIndexClicked < this.lastRecordsIndexClicked ?
            this.lastShiftRecordsIndexClicked : this.lastRecordsIndexClicked;
          const lastIndex = this.lastShiftRecordsIndexClicked < this.lastRecordsIndexClicked ?
            this.lastRecordsIndexClicked : this.lastShiftRecordsIndexClicked;
          for (let i = firstIndex; i <= lastIndex; i++) {
            const recordId = this.recordIds[i];
            const selectedIndex = this.selectedIds.indexOf(recordId);
            if (selectedIndex != -1) {
              const record = this.records.find(value => value && value.id === recordId);
              if (record) record.selected = false;
              this.selectedIds.splice(selectedIndex, 1);
            }
          }
        }
        const firstIndex = clickedRecordsIndex < this.lastRecordsIndexClicked ? clickedRecordsIndex : this.lastRecordsIndexClicked;
        const lastIndex = clickedRecordsIndex < this.lastRecordsIndexClicked ? this.lastRecordsIndexClicked : clickedRecordsIndex;
        for (let i = firstIndex; i <= lastIndex; i++) {
          const recordId = this.recordIds[i];
          if (this.selectedIds.indexOf(recordId) == -1) {
            this.selectedIds.push(recordId);
            const record = this.records.find(value => value && value.id === recordId);
            if (record) record.selected = true;
          }
        }
        this.lastShiftRecordsIndexClicked = clickedRecordsIndex;
      }
    }
    if (!shiftPressed || this.lastRecordsIndexClicked === -1) {
      this.lastRecordsIndexClicked = clickedRecordsIndex;
      this.lastShiftRecordsIndexClicked = -1;
    }
    this.selectionType = 'CUSTOM';
    this.selectedIdsDebounceSubject.next(this.selectedIds);
  }

  public clearSelection(emitEvent: boolean = true) {
    this.records.filter(value => value && value.selected).forEach(record => record.selected = false);
    this.selectedIds = [];
    this.lastRecordsIndexClicked = -1;
    this.lastShiftRecordsIndexClicked = -1;
    this.lastMouseSelect = false;
    if (emitEvent) {
      this.selectionType = 'CUSTOM';
      this.selectedIdsDebounceSubject.next(this.selectedIds);
    }
  }

  public reloadTableValueMap() {
    this.tableValueMap['hasFilter'] = this.hasFilter();
    this.tableValueMap['hasSort'] = this.hasSort();
    this.headerButtonConfig.forEach((conf, index) => {
      if (conf.enabled) this.tableValueMap['bttHeaderEnabled-' + index] = conf.enabled()
    });
    this.footerButtonConfig.forEach((conf, index) => {
      if (conf.enabled) this.tableValueMap['bttFooterEnabled-' + index] = conf.enabled()
    });
    super.reloadTableValueMap();
  }

  @HostListener('document:keydown.control.a', ['$event'])
  onSelectAll(e: KeyboardEvent) {
    if (this.disableSelection) return;
    if (!this.focused) return;
    this.selectedIds = [...this.recordIds];
    this.records.filter(value => value && !value.selected).forEach(record => record.selected = true);
    this.selectionType = 'CTRLA';
    this.selectedIdsDebounceSubject.next(this.selectedIds);
  }

  colOrderChanged($event: any) {
    console.log("----> colOrderChanged")
    this.onColOrderChange.emit(this.ccTable.columns);
  }

  override onShCtFilter() {
    super.onShCtFilter();
    this.reloadTableValueMap();
    this.filterChangedEvent.emit();
  }

  onFilterChange() {
    const newRequest = this.triggerRemoteRefreshFromUi(true);
    this.filterChangedEvent.emit(newRequest);
  }

  onSortChange() {
    this.reloadTableValueMap();
    // setTimeout(() => this.reloadTableValueMap());
    this.sortChangedEvent.emit();
  }

  // onSortChange($event: any) {
  //   this.ccTable?.scrollTo({
  //     left: 0,
  //     top: 0
  //   })
  //   this.ccTable.sortField = $event.field;
  //   this.ccTable.sortOrder = $event.order;
  //   this.ccTable._sortOrder = $event.order;
  //   this.ccTable._sortField = $event.field;
  //   console.log('----> onSortChange ',JSON.stringify($event))
  //   const newRequest = this.triggerRemoteRefreshFromUi(true);
  //   this.filterChangedEvent.emit(newRequest);
  //   this.reloadTableValueMap();
  //   // setTimeout(() => this.reloadTableValueMap());
  //   this.sortChangedEvent.emit();
  // }
  // onSortChange(event?: any) {
  //   const sortField =
  //     event?.field ??
  //     event?.sortField ??
  //     this.ccTable?.sortField ??
  //     this.currentRequest?.sortField ??
  //     this.getDefaultSortField();
  //
  //   const sortOrder =
  //     event?.order ??
  //     event?.sortOrder ??
  //     this.ccTable?.sortOrder ??
  //     this.currentRequest?.sortOrder ??
  //     this.getDefaultSortOrder();
  //
  //   const multiSortMeta = event?.multiSortMeta ?? undefined;
  //
  //   this.ccTable.sortField = sortField;
  //   this.ccTable.sortOrder = sortOrder;
  //   (this.ccTable as any).multiSortMeta = multiSortMeta;
  //
  //   const newRequest = this.buildRequestFromUi(true);
  //   newRequest.sortField = sortField;
  //   newRequest.sortOrder = sortOrder;
  //   newRequest.multiSortMeta = multiSortMeta;
  //
  //   this.setFiltersAndSort(newRequest);
  //   this.loadRecordsFromRequest(newRequest);
  //   this.reloadTableValueMap();
  //   this.sortChangedEvent.emit(newRequest);
  // }

  onGlobalFilterInput() {
    this.updateGlobalFilterState(this.globalFilterValue);
    const newRequest = this.triggerRemoteRefreshFromUi(true);
    this.filterChangedEvent.emit(newRequest);
  }

  // override scrollToTop() {
  //   if (this.tableScroll.left != 0) {
  //     this.ccTable?.scrollTo({
  //       left: this.tableScroll.left,
  //       top: 0
  //     })
  //   }
  // }
  // override loadRecordsFromRequest(newRequest: TableRequest) {
  //   // super.loadRecordsFromRequest(newRequest);
  //   //
  //   if (!this.currentRequest || JSON.stringify(this.currentRequest.originEvent) != JSON.stringify(newRequest.originEvent)) {
  //     this.lastLazyLoadEvent = JSON.parse(JSON.stringify(newRequest));
  //     super.loadRecordsFromRequest(newRequest);
  //   }
  // }
}
